import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useRef, useState } from 'react'
import { Input2, Pagination } from 'stories/components'
import { formatDate } from 'utils'

import { ledgerMoney } from './ledgerSort'
import type { ITrustAccountLedgerResponse, ITrustAccountLedgerRow } from './types'

const COLUMNS: Array<{ key: string; title: string; align?: string }> = [
  { key: 'trustAccountName', title: 'Trust Account' },
  { key: 'date', title: 'Date' },
  { key: 'loanNumber', title: 'Loan' },
  { key: 'category', title: 'Category' },
  { key: 'type', title: 'Type' },
  { key: 'reference', title: 'Reference' },
  { key: 'fromWhom', title: 'From Whom Received/Paid' },
  { key: 'payment', title: 'Payment', align: 'text-right' },
  { key: 'deposit', title: 'Deposit', align: 'text-right' },
  { key: 'balance', title: 'Balance', align: 'text-right' },
]

/**
 * The trust ledger for an account or for a lender/vendor file.
 *
 * Search, sort and paging all run on the backend, which strikes the running balance over the
 * whole account in date order before narrowing anything. Filtering or paging in the browser would
 * renumber the Balance column.
 */
export default function TrustLedgerGrid({
  fetchRows,
  emptyText = 'No transactions',
  onSelectRow,
  onTotals,
  refreshKey = 0,
}: {
  fetchRows: (filters: Record<string, any>) => Promise<ITrustAccountLedgerResponse>
  emptyText?: string
  /** Set to let the caller edit an account-level entry; loan rows are never editable here. */
  onSelectRow?: (row: ITrustAccountLedgerRow) => void
  /**
   * The balance struck for each account, handed up as it is loaded. The grid is the only place
   * that has it, and the entry modal beside it needs it to tell whether a withdrawal is covered.
   */
  onTotals?: (totals: ITrustAccountLedgerResponse['totals']) => void
  /** Bump to reload after the caller writes an entry. */
  refreshKey?: number
}) {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<ITrustAccountLedgerRow[]>([])
  const [totals, setTotals] = useState<ITrustAccountLedgerResponse['totals']>([])
  const [balance, setBalance] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const [query, setQuery] = useState('')
  const [orderBy, setOrderBy] = useState('date')
  // Newest transaction first, same as the loan's own Trust Ledger grids.
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc')
  const [pageNum, setPageNum] = useState(0)
  const [itemCountPerPage, setItemCountPerPage] = useState(25)

  const [debouncedQuery, setDebouncedQuery] = useState('')
  const firstRender = useRef(true)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
      if (!firstRender.current) setPageNum(0)
    }, 350)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    firstRender.current = false
    setLoading(true)
    fetchRows({ query: debouncedQuery, orderBy, orderDir, pageNum, count: itemCountPerPage })
      .catch(() => ({ results: [], totalCount: 0, totals: [], balance: 0 }) as ITrustAccountLedgerResponse)
      .then((res) => {
        setRows(res?.results ?? [])
        setTotalCount(res?.totalCount ?? 0)
        setTotals(res?.totals ?? [])
        setBalance(res?.balance ?? 0)
        onTotals?.(res?.totals ?? [])
      })
      .finally(() => setLoading(false))
  }, [debouncedQuery, orderBy, orderDir, pageNum, itemCountPerPage, refreshKey])

  const onSort = (key: string) => {
    if (orderBy === key) setOrderDir(orderDir === 'asc' ? 'desc' : 'asc')
    else {
      setOrderBy(key)
      setOrderDir('desc')
    }
    setPageNum(0)
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <div className="flex justify-between items-end flex-wrap gap-2">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-64">
            <Input2
              type="search"
              title="Search all columns"
              value={query}
              onChange={(value: string) => setQuery(value)}
            />
          </div>
          <span className="text-[13px] text-gray-500 pb-2">
            {totalCount} transaction{totalCount === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-end gap-4 flex-wrap pb-1">
          {totals.map((item) => (
            <div key={item.trustAccountId} className="text-[13px] text-gray-500">
              {item.name}: <span className="text-gray-900">{ledgerMoney(item.balance)}</span>
            </div>
          ))}
          {totals.length > 1 && (
            <div className="text-[14px] font-variation-settings-600">Total {ledgerMoney(balance)}</div>
          )}
        </div>
      </div>

      <div className="mt-2 overflow-auto shadow-md rounded">
        <table className="w-full text-[14px]">
          <thead className="font-medium text-gray-600 bg-gray-100">
            <tr className="text-left">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2 cursor-pointer hover:text-shade-blue select-none ${col.align || ''}`}
                  onClick={() => onSort(col.key)}
                >
                  <span
                    className={`inline-flex items-center gap-1 ${col.align === 'text-right' ? 'flex-row-reverse' : ''}`}
                  >
                    {col.title}
                    {orderBy === col.key &&
                      (orderDir === 'asc' ? (
                        <ChevronUpIcon className="w-3 h-3" />
                      ) : (
                        <ChevronDownIcon className="w-3 h-3" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.source}-${row.trustAccountId}-${row.id}-${index}`}
                className={`${index % 2 ? 'bg-gray-50' : ''} ${
                  onSelectRow && row.source === 'entry' ? 'cursor-pointer hover:bg-shade-blue/5' : ''
                }`}
                onClick={() => row.source === 'entry' && onSelectRow?.(row)}
              >
                <td className="px-4 py-2 whitespace-nowrap">{row.trustAccountName || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.date ? formatDate(row.date) : ''}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.loanNumber || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.category}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.type}</td>
                <td className="px-4 py-2">{row.reference}</td>
                <td className="px-4 py-2">{row.fromWhom}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.payment)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.deposit)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{ledgerMoney(row.balance)}</td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={COLUMNS.length}>
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end items-center mt-3 mb-3">
        <Pagination
          totalCount={totalCount}
          itemCountPerPage={itemCountPerPage}
          onNavigate={setPageNum}
          pageNum={pageNum}
          onCountPerPageUpdate={(count: number) => {
            setItemCountPerPage(count)
            setPageNum(0)
          }}
        />
      </div>
    </div>
  )
}
